import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Check, Edit3, ZoomIn, MapPin, Clock, Calendar, Hash, Truck, Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/Sheet';
import {
  useOcrResult,
  useAcceptOcr,
  useCorrectOcr,
  useRejectOcr,
  type OcrResult,
} from '@/hooks/useOcr';
import { ConfidencePill } from './ConfidencePill';

type Editable = {
  m3_value: string;
  voucher_number: string;
  ocr_time: string;
  ocr_date: string;
};

interface OcrReviewDialogProps {
  ocrResultId: string | null;
  onClose: () => void;
  onProcessed?: () => void;
}

export function OcrReviewDialog({
  ocrResultId,
  onClose,
  onProcessed,
}: OcrReviewDialogProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useOcrResult(ocrResultId);
  const accept = useAcceptOcr();
  const correct = useCorrectOcr();
  const reject = useRejectOcr();

  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState<Editable>({
    m3_value: '', voucher_number: '', ocr_time: '', ocr_date: '',
  });
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    if (!data) return;
    setVals({
      m3_value: data.m3_value || '',
      voucher_number: data.voucher_number || '',
      ocr_time: data.ocr_time || '',
      ocr_date: data.ocr_date || '',
    });
    setEditing(false);
  }, [data]);

  const onAcceptAll = async () => {
    if (!data) return;
    await accept.mutateAsync(data.id);
    onProcessed?.();
  };

  const onSaveCorrections = async () => {
    if (!data) return;
    const patch: {
      m3_value?: number;
      voucher_number?: string;
      ocr_time?: string;
      ocr_date?: string;
    } = {};
    if (vals.m3_value && vals.m3_value !== data.m3_value) patch.m3_value = Number(vals.m3_value);
    if (vals.voucher_number !== (data.voucher_number || '')) patch.voucher_number = vals.voucher_number;
    if (vals.ocr_time !== (data.ocr_time || '')) patch.ocr_time = vals.ocr_time;
    if (vals.ocr_date !== (data.ocr_date || '')) patch.ocr_date = vals.ocr_date;

    if (Object.keys(patch).length === 0) {
      await onAcceptAll();
      return;
    }
    await correct.mutateAsync({ id: data.id, patch });
    onProcessed?.();
  };

  const onReject = async () => {
    if (!data) return;
    if (!confirm(t('ocr.confirm_reject'))) return;
    await reject.mutateAsync({ id: data.id });
    onProcessed?.();
  };

  // A3 fix: <img> tags can't send Authorization headers, so we fetch the image as a blob
  // (with JWT) and convert to an object URL. Cleans up the URL when the dialog closes.
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setPhotoError(null);
    setPhotoUrl(null);
    if (!data?.trip_photo_id || !data?.trip_id) return;
    const base = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';
    const tok = localStorage.getItem('jasrhub_access_token');
    fetch(`${base}/api/v1/trips/${data.trip_id}/photos/${data.trip_photo_id}/file`, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPhotoUrl(objectUrl);
      })
      .catch((err) => {
        if (!cancelled) setPhotoError(err instanceof Error ? err.message : 'Failed to load image');
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [data?.trip_photo_id, data?.trip_id]);

  return (
    <Sheet open={!!ocrResultId} onOpenChange={(o: boolean) => !o && onClose()}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t('ocr.dialog.title')}</SheetTitle>
          <SheetDescription className="font-mono text-xs">
            {data ? `${data.trip_code || ''} · ${data.trip_date ? new Date(data.trip_date).toLocaleDateString('en-GB') : ''}` : ''}
          </SheetDescription>
        </SheetHeader>

        {isLoading || !data ? (
          <div className="flex h-64 items-center justify-center">
            <div className="text-sm text-muted-foreground">Loading…</div>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {/* Voucher photo */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium">{t('ocr.dialog.voucher_image')}</h3>
                <button
                  onClick={() => setZoom(!zoom)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  <ZoomIn className="inline h-3.5 w-3.5" /> {t('common.zoom')}
                </button>
              </div>
              {photoUrl ? (
                <div className={`overflow-hidden rounded-md border border-border ${zoom ? 'max-h-96 overflow-auto' : ''}`}>
                  <img
                    src={photoUrl}
                    alt={t('ocr.dialog.voucher_image')}
                    className={zoom ? 'min-w-[120%] max-w-none' : 'h-full w-full object-contain'}
                  />
                </div>
              ) : photoError ? (
                <div className="flex h-64 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-rose-500/40 text-sm text-rose-600 dark:text-rose-400">
                  <span>Image unavailable</span>
                  <span className="text-xs text-muted-foreground">{photoError}</span>
                </div>
              ) : data?.trip_photo_id ? (
                <div className="flex h-64 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                  Loading…
                </div>
              ) : (
                <div className="flex h-64 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                  {t('ocr.dialog.no_photo')}
                </div>
              )}

              {data.failure_reason && (
                <div className="mt-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                  {t(`ocr.failure_reason.${data.failure_reason}`)}
                  {data.failure_detail && (
                    <div className="mt-1 font-mono">{data.failure_detail}</div>
                  )}
                </div>
              )}
            </div>

            {/* Extracted fields */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">{t('ocr.dialog.extracted_fields')}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(!editing)}
                >
                  <Edit3 className="mr-1 h-3.5 w-3.5" />
                  {editing ? t('common.cancel') : t('common.edit')}
                </Button>
              </div>

              <FieldRow
                icon={<Hash className="h-4 w-4" />}
                label={t('ocr.field.m3')}
                value={vals.m3_value}
                onChange={(v) => setVals({ ...vals, m3_value: v })}
                rawText={data.m3_raw}
                confidence={data.m3_confidence}
                editing={editing}
                inputMode="decimal"
                placeholder="28"
              />
              <FieldRow
                icon={<Hash className="h-4 w-4" />}
                label={t('ocr.field.voucher_number')}
                value={vals.voucher_number}
                onChange={(v) => setVals({ ...vals, voucher_number: v })}
                rawText={data.voucher_raw}
                confidence={data.voucher_confidence}
                editing={editing}
                placeholder="5260"
              />
              <FieldRow
                icon={<Clock className="h-4 w-4" />}
                label={t('ocr.field.time')}
                value={vals.ocr_time}
                onChange={(v) => setVals({ ...vals, ocr_time: v })}
                rawText={data.time_raw}
                confidence={data.time_confidence}
                editing={editing}
                placeholder="04:15"
                inputType="time"
              />
              <FieldRow
                icon={<Calendar className="h-4 w-4" />}
                label={t('ocr.field.date')}
                value={vals.ocr_date}
                onChange={(v) => setVals({ ...vals, ocr_date: v })}
                rawText={data.date_raw}
                confidence={data.date_confidence}
                editing={editing}
                inputType="date"
                badge={data.date_source === 'exif' ? t('ocr.from_exif') : undefined}
              />

              {/* Read-only secondary fields */}
              <div className="rounded-md bg-muted p-2 text-xs text-muted-foreground space-y-1">
                {data.vehicle_plate && (
                  <div className="flex items-center gap-2">
                    <Truck className="h-3 w-3" /> {data.vehicle_plate}
                    <ConfidencePill confidence={data.vehicle_confidence} compact />
                  </div>
                )}
                {data.driver_name && (
                  <div className="flex items-center gap-2">
                    <Pencil className="h-3 w-3" /> {data.driver_name}
                    <ConfidencePill confidence={data.driver_confidence} compact />
                  </div>
                )}
                {data.site_code && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3 w-3" /> {data.site_code}
                    <ConfidencePill confidence={data.site_confidence} compact />
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2 pt-2 sm:flex-row">
              <Button onClick={onSaveCorrections} className="flex-1" disabled={correct.isPending || accept.isPending}>
                <Check className="mr-1 h-4 w-4" />
                {editing ? t('ocr.dialog.save_corrections') : t('ocr.dialog.accept_all')}
              </Button>
              <Button onClick={onReject} variant="ghost" disabled={reject.isPending}>
                <X className="mr-1 h-4 w-4" />
                {t('ocr.dialog.reject')}
              </Button>
            </div>

            <div className="text-xs text-muted-foreground">
              {t('ocr.dialog.engine_meta', {
                engine: data.engine,
                ms: data.ocr_duration_ms || 0,
              })}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

interface FieldRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
  rawText: string | null;
  confidence: string | null;
  editing: boolean;
  badge?: string;
  inputMode?: 'text' | 'decimal' | 'numeric';
  inputType?: string;
  placeholder?: string;
}

function FieldRow({
  icon, label, value, onChange, rawText, confidence, editing, badge,
  inputMode, inputType, placeholder,
}: FieldRowProps) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="text-foreground">{icon}</span>
          {label}
          {badge && (
            <span className="rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px] text-blue-600 dark:text-blue-400">
              {badge}
            </span>
          )}
        </div>
        <ConfidencePill confidence={confidence} />
      </div>
      <div className="mt-2">
        {editing ? (
          <Input
            type={inputType || 'text'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            inputMode={inputMode}
            placeholder={placeholder}
            className="font-mono text-sm"
          />
        ) : (
          <div className="font-mono text-base">
            {value || <span className="text-muted-foreground">—</span>}
          </div>
        )}
        {rawText && rawText !== value && (
          <div className="mt-1 text-[11px] text-muted-foreground">
            raw: <span className="font-mono">{rawText}</span>
          </div>
        )}
      </div>
    </div>
  );
}
